import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useSession } from '@/hooks/useSession';
import { selectCurrentGroupId } from '@/store/selectors';
import {
  getAllMessages,
  getMessagesForCurrentUserGroup,
} from './notificationUtils';

export function useNotifications() {
  const [allMessages, setAllMessages] = useState<unknown[]>([]);
  const [currentGroupMessages, setCurrentGroupMessages] = useState<unknown[]>([]);

  const { isAuthenticated } = useSession();
  const currentUserGroup = useSelector(selectCurrentGroupId);
  const location = useLocation();

  const fetchNotifications = useCallback(() => {
    if (!isAuthenticated) return;

    Promise.all([getAllMessages(), getMessagesForCurrentUserGroup()]).then(
      ([all, current]) => {
        if (all) setAllMessages(all as unknown[]);
        if (current) setCurrentGroupMessages(current as unknown[]);
      },
    );
  }, [isAuthenticated]);

  // Initial fetch with 2-second delay + refetch on group/path change
  useEffect(() => {
    const timeout = setTimeout(fetchNotifications, 2000);
    return () => clearTimeout(timeout);
  }, [currentUserGroup, location.pathname, fetchNotifications]);

  // Poll every 15 seconds
  useEffect(() => {
    if (!isAuthenticated) return;

    const interval = setInterval(fetchNotifications, 15000);
    return () => clearInterval(interval);
  }, [isAuthenticated, fetchNotifications]);

  return { allMessages, currentGroupMessages };
}
